/* eslint-disable @typescript-eslint/no-shadow */

import { useTranslation } from 'react-i18next';
import {
  Calendar, Cancel, ChevronDown
} from '@huspy/briks-icons';
import {
  Chip, DatePicker, DatePickerRootProps, Flex, Text
} from '@huspy/briks-web';

const getKeyPostfix = (id: number) => id;

const DateRange = ({ ...props }: DatePickerRootProps) => {
  const { t } = useTranslation();

  return (
    <DatePicker.Root { ...props } selectionMode='range'>
      <DatePicker.Control>
        <DatePicker.Trigger cursor='pointer'>
          <Chip variant='glassy' cursor='pointer'>
            <DatePicker.Label cursor='pointer'>
              <Flex paddingX={ 5 } align='center' gap={ 2 }>
                {
                  props.value?.[0] ? (
                    <>
                      <Calendar />
                      <Text size='md'>
                        { props.value[0] ?? '' }
                        {' - '}
                        { props.value[1] ?? '' }
                      </Text>
                      <DatePicker.ClearTrigger>
                        <Cancel />
                      </DatePicker.ClearTrigger>
                    </>

                  ) : (
                    <>
                      <Calendar />
                      <Text size='md'>{ t('common.forms.dates.range.placeholder') }</Text>
                      <ChevronDown />
                    </>
                  )
                }
              </Flex>
            </DatePicker.Label>
          </Chip>
        </DatePicker.Trigger>
      </DatePicker.Control>
      <DatePicker.Positioner>
        <DatePicker.Content>
          <DatePicker.YearSelect />
          <DatePicker.MonthSelect />
          <DatePicker.View view='day'>
            <DatePicker.Context>
              {(datePicker) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger>
                      <DatePicker.RangeText />
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableHead>
                      <DatePicker.TableRow>
                        {datePicker.weekDays.map((weekDay) => (
                          <DatePicker.TableHeader key={ weekDay.short }>
                            {weekDay.short}
                          </DatePicker.TableHeader>
                        ))}
                      </DatePicker.TableRow>
                    </DatePicker.TableHead>
                    <DatePicker.TableBody>
                      {datePicker.weeks.map((week, id) => (
                        <DatePicker.TableRow key={ `week-${getKeyPostfix(id)}` }>
                          {week.map((day) => (
                            <DatePicker.TableCell key={ day.day } value={ day }>
                              <DatePicker.TableCellTrigger>{day.day}</DatePicker.TableCellTrigger>
                            </DatePicker.TableCell>
                          ))}
                        </DatePicker.TableRow>
                      ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
          <DatePicker.View view='month'>
            <DatePicker.Context>
              {(datePicker) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger>
                      <DatePicker.RangeText />
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableBody>
                      {datePicker
                        .getMonthsGrid({ columns: 4, format: 'short' })
                        .map((months, id) => (
                          <DatePicker.TableRow key={ `month-row-${getKeyPostfix(id)}` }>
                            {months.map((month) => (
                              <DatePicker.TableCell
                                key={ `month-cell-${month.value}` }
                                value={ month.value }
                              >
                                <DatePicker.TableCellTrigger>
                                  {month.label}
                                </DatePicker.TableCellTrigger>
                              </DatePicker.TableCell>
                            ))}
                          </DatePicker.TableRow>
                        ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
          <DatePicker.View view='year'>
            <DatePicker.Context>
              {(datePicker) => (
                <>
                  <DatePicker.ViewControl>
                    <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                    <DatePicker.ViewTrigger>
                      <DatePicker.RangeText />
                    </DatePicker.ViewTrigger>
                    <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                  </DatePicker.ViewControl>
                  <DatePicker.Table>
                    <DatePicker.TableBody>
                      {datePicker.getYearsGrid({ columns: 4 }).map((years, id) => (
                        <DatePicker.TableRow key={ `year-row-${getKeyPostfix(id)}` }>
                          {years.map((year) => (
                            <DatePicker.TableCell
                              key={ `year-cell-${year.value}` }
                              value={ year.value }
                            >
                              <DatePicker.TableCellTrigger>
                                {year.label}
                              </DatePicker.TableCellTrigger>
                            </DatePicker.TableCell>
                          ))}
                        </DatePicker.TableRow>
                      ))}
                    </DatePicker.TableBody>
                  </DatePicker.Table>
                </>
              )}
            </DatePicker.Context>
          </DatePicker.View>
        </DatePicker.Content>
      </DatePicker.Positioner>
    </DatePicker.Root>
  );
};

export default DateRange;
